import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { isValidPhoneNumber } from "libphonenumber-js/mobile";
import CustomPhoneInput from "./CustomPhoneInput";
import ButtonComp from "./ButtonComp";
import API from "api";
import "../../assets/css/PteConsultation.css";

const EMAIL_REGEX = /^[a-zA-Z0-9]([a-zA-Z0-9._%+\-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9\-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9\-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
// Allow Unicode letters (\p{L}) + combining marks (\p{M}) so diacritics like
// é/ñ survive whether they're precomposed or decomposed, plus space, apostrophe, hyphen.
const NAME_DISALLOWED_REGEX = /[^\p{L}\p{M} '\-]/gu;
const NAME_ALLOWED_REGEX = /^[\p{L}\p{M} '\-]+$/u;
 
const normalizeName = (value) => (value || "").toString().normalize("NFC");
 
const validateField = (field, value) => {
  switch (field) {
    case "fullName": {
      const v = normalizeName(value).trim();
      if (!v) return "Name is required.";
      if (!NAME_ALLOWED_REGEX.test(v)) {
        return "Please use letters, spaces, apostrophes, or hyphens only.";
      }
      return "";
    }
    case "email": {
      const v = (value || "").toString().trim();
      if (!v) return "Email is required.";
      if (!EMAIL_REGEX.test(v) || /\.\./.test(v.split("@")[0])) return "Please enter a valid email address.";
      return "";
    }
    case "phone": {
      const v = (value || "").toString().trim();
      if (!v) return "Phone Number is required.";
      if (!isValidPhoneNumber(v)) {
        return "Please enter a valid phone number for the selected country.";
      }
      return "";
    }
    case "branch":
      if (!value) return "Please select a branch.";
      return "";
    default:
      return "";
  }
};

const BULLETS = [
  { strong: "50,000+", rest: "students trained" },
  { strong: "85%", rest: "achieve target score on first attempt" },
  { strong: "8+ years", rest: "of PTE coaching experience" },
  { strong: "4.8★", rest: "Google Rating" },
];

const PteConsultation = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    branch: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [branches, setBranches] = useState([]);
  const submittingRef = useRef(false); // synchronous lock against rapid double-clicks

  useEffect(() => {
    let cancelled = false;
    API({ url: "getAllBranches", method: "GET" })
      .then((res) => {
        if (cancelled) return;
        if (res?.status === 200 && Array.isArray(res.data?.branches)) {
          setBranches(res.data.branches);
        }
      })
      .catch(() => {
        // silently ignore — the dropdown just stays empty
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      // clear the error as soon as the user starts fixing it
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleChange = (field) => (e) => setField(field, e.target.value);
  const handleNameChange = (e) =>
    setField("fullName", normalizeName(e.target.value).replace(NAME_DISALLOWED_REGEX, ""));
  const handleBlur = (field) => () => {
    const message = validateField(field, formData[field]);
    setErrors((prev) => {
      const next = { ...prev };
      if (message) next[field] = message;
      else delete next[field];
      return next;
    });
  };
  const handlePhoneChange = (value) => setField("phone", value);

  const validateAll = () => {
    const next = {};
    Object.keys(formData).forEach((field) => {
      const message = validateField(field, formData[field]);
      if (message) next[field] = message;
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (submittingRef.current) return; // synchronous block — runs before re-render

    if (!validateAll()) return;

    submittingRef.current = true;
    setSubmitting(true);

    const body = new FormData();
    body.append("name", formData.fullName);
    body.append("email", formData.email);
    body.append("phone", formData.phone);
    body.append("center", formData.branch);

    try {
      const res = await API({ method: "POST", url: "/submit-query", data: body });
      if (res?.status === 200) {
        toast.success("Thanks! Our team will contact you shortly.");
        setFormData({ fullName: "", email: "", phone: "", branch: "" });
        setErrors({});
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      if (err?.response?.status === 422 && err.response.data?.errors) {
        // Map server validation errors back onto the matching fields (no toast)
        const serverErrors = err.response.data.errors;
        const mapped = {};
        if (serverErrors.name) mapped.fullName = serverErrors.name[0];
        if (serverErrors.email) mapped.email = serverErrors.email[0];
        if (serverErrors.phone) mapped.phone = serverErrors.phone[0];
        if (serverErrors.center) mapped.branch = serverErrors.center[0];
        setErrors((prev) => ({ ...prev, ...mapped }));
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <section className="pte-consultation custom-container">
      <div className="pte-consultation__container">
        <div className="pte-consultation__intro">
          <h3 className="pte-consultation__title">
            Get Your Free{" "}
            <em className="pte-consultation__title-accent">PTE Consultation</em>
          </h3>
          <p className="pte-consultation__subtitle">
            Start your journey with a personalized strategy session.
          </p>
          <ul className="feature-list plus-sans FS16FW500 fw-400 m-0">
            {BULLETS.map((bullet, i) => (
              <li key={i} className="pte-consultation__bullet">
                <span>
                  <strong>{bullet.strong}</strong> {bullet.rest}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <form className="pte-consultation__form" onSubmit={handleSubmit} noValidate>
          <div className="pte-consultation__row">
            <label className="pte-consultation__field">
              <span className="pte-consultation__label">Full Name</span>
              <input
                type="text"
                className={`pte-consultation__input${
                  errors.fullName ? " pte-consultation__input--invalid" : ""
                }`}
                placeholder="Enter name"
                value={formData.fullName}
                onChange={handleNameChange}
                onBlur={handleBlur("fullName")}
                aria-invalid={!!errors.fullName}
                maxLength={30}
              />
              {errors.fullName && (
                <span className="pte-consultation__error">{errors.fullName}</span>
              )}
            </label>
            <label className="pte-consultation__field">
              <span className="pte-consultation__label">Email Address</span>
              <input
                type="email"
                className={`pte-consultation__input${
                  errors.email ? " pte-consultation__input--invalid" : ""
                }`}
                placeholder="Enter email address"
                value={formData.email}
                onChange={handleChange("email")}
                onBlur={handleBlur("email")}
                aria-invalid={!!errors.email}
              />
              {errors.email && (
                <span className="pte-consultation__error">{errors.email}</span>
              )}
            </label>
          </div>

          <div className="pte-consultation__row">
            <div
              className={`pte-consultation__field${
                errors.phone ? " pte-consultation__field--invalid" : ""
              }`}
            >
              <span className="pte-consultation__label">Phone Number</span>
              <CustomPhoneInput
                value={formData.phone}
                onChange={handlePhoneChange}
                onBlur={handleBlur("phone")}
              />
              {errors.phone && (
                <span className="pte-consultation__error">{errors.phone}</span>
              )}
            </div>
            <label className="pte-consultation__field">
              <span className="pte-consultation__label">Branch</span>
              <select
                className={`pte-consultation__input pte-consultation__select${
                  errors.branch ? " pte-consultation__input--invalid" : ""
                }`}
                value={formData.branch}
                onChange={handleChange("branch")}
                onBlur={handleBlur("branch")}
                aria-invalid={!!errors.branch}
              >
                <option value="">Select branch</option>
                {branches.map((branch) => (
                  branch.branch_detail?.title && (
                    <option key={branch.id} value={branch.branch_detail.title}>
                      {branch.branch_detail.title}
                    </option>
                  )
                ))}
                <option value="Online">Online</option>
              </select>
              {errors.branch && (
                <span className="pte-consultation__error">{errors.branch}</span>
              )}
            </label>
          </div>

          <div
            className={`pte-consultation__submit-wrap${
              submitting ? " pte-consultation__submit-wrap--disabled" : ""
            }`}
          >
            <ButtonComp
              btnText={submitting ? "Submitting..." : "Submit Application"}
              link={submitting ? () => {} : handleSubmit}
            />
          </div>
        </form>
      </div>
    </section>
  );
};

export default PteConsultation;
